"use client";

import * as React from "react";
import { useUser } from "@clerk/react";
import { createAppUserSettingsStorageManager, UserThemeSettingsSync } from "@scoutingops/ui";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation } from "react-router";

import { appConfig } from "@/config/app";
import { createTheme } from "@/styles/theme/create-theme";
import { getCustomDomainHostname } from "@/lib/custom-domain";

export interface ThemeProviderProps {
	children: React.ReactNode;
}

// Only the authenticated /studio area (config pages) gets a persisted
// per-user dark/light preference — the marketing site and public unit pages
// always render appConfig's default, matching the rest of the platform's
// marketing-vs-app split (see hub's src/components/core/theme-provider.tsx).
function isAppRoute(pathname: string): boolean {
	return pathname === "/studio" || pathname.startsWith("/studio/");
}

// A published unit site (/site/:slug on our own host, or any path on a
// verified custom domain) must never be nested inside the platform's own
// CssVarsProvider/CssBaseline — PublicSite.tsx supplies its own theme and
// its own CssBaseline (see create-unit-site-theme.ts), and if the platform
// theme is still the ambient ancestor, its CssBaseline still paints
// <body>'s background/text colors globally (visible at the page edges, on
// mobile overscroll, and as a flash before the unit theme's own reset
// applies) even though individual components correctly pick up the unit's
// theme values. The fix is to not wrap these routes in the platform theme
// at all, rather than trying to override every leaked value downstream.
function isPublicSiteRoute(pathname: string): boolean {
	return Boolean(getCustomDomainHostname()) || pathname.startsWith("/site/");
}

function CustomThemeProvider({ children }: ThemeProviderProps): React.JSX.Element {
	const { isLoaded, user } = useUser();
	const { pathname } = useLocation();
	const appRoute = isAppRoute(pathname);
	const userId = isLoaded ? user?.id : undefined;

	const storageManager = React.useMemo(
		() => (appRoute ? createAppUserSettingsStorageManager(userId) : null),
		[appRoute, userId]
	);

	const theme = React.useMemo(
		() => createTheme({ direction: appConfig.direction, primaryColor: appConfig.primaryColor }),
		[]
	);

	if (isPublicSiteRoute(pathname)) {
		return <>{children}</>;
	}

	return (
		<ThemeProvider
			defaultMode={appConfig.theme}
			disableTransitionOnChange
			key={appRoute ? `app-${userId ?? "anonymous"}` : "public-default"}
			storageManager={storageManager}
			theme={theme}
		>
			<CssBaseline />
			{appRoute ? <UserThemeSettingsSync defaultMode={appConfig.theme} /> : null}
			{children}
		</ThemeProvider>
	);
}

export { CustomThemeProvider as ThemeProvider };
